"use client";

import Link from "next/link";
import { PeeqdLogoIcon } from "./icons";
import config from "@/config";
import ButtonCheckout from "@/components/landing/ButtonCheckout";
import { PeeqdFaqItem, PEEQD_FAQ } from "@/config/peeqdContent";
import {
  billableAmountLabel,
  billingCadenceLabel,
  formatUsd,
  monthlyEquivalentPrice,
  priceIdForInterval,
  weeklyEquivalentPrice,
  type BillingInterval,
} from "@/libs/stripePlans";

export type PricingPriceLayout = "interval-weekly" | "interval-monthly";

interface PeeqdPricingProps {
  currentUserPriceId?: string | null;
  className?: string;
  /** Initial display mode. interval-weekly: /week on cards. interval-monthly: /month on cards. */
  priceLayout?: PricingPriceLayout;
}

const proPlan = config.stripe.plans.find((p) => p.name === "Pro");

const BILLING_CARDS: {
  id: BillingInterval;
  label: string;
  badge?: string;
  featured?: boolean;
}[] = [
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly", badge: "Most popular", featured: true },
  { id: "annual", label: "Annual", badge: "Best value" },
];

function PlanButton({
  priceId,
  label,
  isCurrent,
  featured,
}: {
  priceId: string;
  label: string;
  isCurrent: boolean;
  featured?: boolean;
}) {
  if (isCurrent) {
    return (
      <div
        className={`mt-5 block w-full rounded-xl py-2.5 text-center text-[14px] font-semibold ${
          featured
            ? "border border-mint/30 bg-mint-soft text-mint"
            : "border border-mint/30 bg-mint-soft text-mint"
        }`}
      >
        Current plan
      </div>
    );
  }

  return (
    <ButtonCheckout
      priceId={priceId}
      mode="subscription"
      text={label}
      className={
        featured
          ? "mt-5 block w-full rounded-xl grad-bg py-2.5 text-center text-[14px] font-semibold text-white transition hover:opacity-95"
          : "mt-5 block w-full rounded-xl border border-ink/15 py-2.5 text-center text-[14px] font-semibold transition hover:bg-rose-softer"
      }
    />
  );
}

function PricingCard({
  card,
  priceId,
  isCurrent,
  weeklyPrice,
  monthlyPrice,
  cadence,
  billable,
  priceLayout = "interval-weekly",
}: {
  card: (typeof BILLING_CARDS)[number];
  priceId: string;
  isCurrent: boolean;
  weeklyPrice: number;
  monthlyPrice: number;
  cadence: string;
  billable: string;
  priceLayout?: PricingPriceLayout;
}) {
  const featured = card.featured;
  const mutedText = featured ? "text-white/60" : "text-muted";
  const showWeekly = priceLayout === "interval-weekly";
  const heroPrice = showWeekly ? weeklyPrice : monthlyPrice;
  const heroSuffix = showWeekly ? "/week" : "/month";

  return (
    <div
      className={`relative flex h-full flex-col rounded-3xl p-6 ${
        featured
          ? "bg-ink text-white shadow-card"
          : "border border-line bg-surface shadow-soft"
      }`}
    >
      {card.badge && (
        <span
          className={`absolute right-5 top-5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            featured ? "grad-bg text-white" : "border border-line bg-bg text-muted"
          }`}
        >
          {card.badge}
        </span>
      )}

      <p className="font-display text-[17px] font-bold">Pro</p>
      <p className={`mt-0.5 text-[13px] ${featured ? "text-white/60" : "text-muted"}`}>
        {card.label}
      </p>

      <p className="mt-4 font-display text-[38px] font-extrabold leading-none">
        ${formatUsd(heroPrice)}
        <span className={`font-sans text-[16px] font-semibold ${mutedText}`}>
          {heroSuffix}
        </span>
      </p>
      <p className={`mt-1 text-[13px] ${mutedText}`}>
        {cadence} · {billable}
      </p>

      <div className="mt-auto">
        {priceId ? (
          <PlanButton
            priceId={priceId}
            label="Start Pro"
            isCurrent={isCurrent}
            featured={featured}
          />
        ) : (
          <div
            className={`mt-5 block w-full rounded-xl py-2.5 text-center text-[14px] font-semibold ${
              featured ? "grad-bg text-white" : "border border-ink/15"
            }`}
          >
            Start Pro
          </div>
        )}
      </div>
    </div>
  );
}

export function PeeqdPricing({
  currentUserPriceId = null,
  className = "",
  priceLayout = "interval-weekly",
}: PeeqdPricingProps) {
  return (
    <section className={`mx-auto max-w-6xl ${className}`}>
      <div className="grid items-stretch gap-5 md:grid-cols-3">
        {BILLING_CARDS.map((card) => {
          const priceId = proPlan ? priceIdForInterval(proPlan, card.id) : "";
          const isCurrent = !!currentUserPriceId && currentUserPriceId === priceId;
          const weeklyPrice = proPlan
            ? weeklyEquivalentPrice(proPlan, card.id)
            : 0;
          const monthlyPrice = proPlan
            ? monthlyEquivalentPrice(proPlan, card.id)
            : 0;
          const cadence = billingCadenceLabel(card.id);
          const billable = proPlan ? billableAmountLabel(proPlan, card.id) : "";

          return (
            <PricingCard
              key={card.id}
              card={card}
              priceId={priceId}
              isCurrent={isCurrent}
              weeklyPrice={weeklyPrice}
              monthlyPrice={monthlyPrice}
              cadence={cadence}
              billable={billable}
              priceLayout={priceLayout}
            />
          );
        })}
      </div>

      {proPlan && (
        <ul className="mx-auto mt-8 grid max-w-2xl gap-2.5 text-[13.5px] text-muted sm:grid-cols-2">
          {proPlan.features.map((feature) => (
            <li key={feature.name} className="flex gap-2">
              <span className="text-mint">✓</span>
              {feature.name}
            </li>
          ))}
        </ul>
      )}

      <p className="mx-auto mt-8 max-w-2xl text-center text-[13px] leading-relaxed text-muted">
        Subscriptions renew automatically. Cancel in one click from your dashboard - you keep access
        until the end of the paid period.
      </p>
    </section>
  );
}

export function PeeqdFAQ({ items = PEEQD_FAQ }: { items?: PeeqdFaqItem[] }) {
  return (
    <section id="faq" className="mx-auto max-w-3xl scroll-mt-8 px-6 py-20">
      <h2 className="text-center font-display text-4xl font-extrabold tracking-tight md:text-5xl">
        Straight answers.
      </h2>
      <div className="mt-10 divide-y divide-black/10">
        {items.map((item) => (
          <details key={item.question} className="group py-5" open={item.defaultOpen}>
            <summary className="flex cursor-pointer list-none items-center justify-between font-semibold">
              {item.question}
              <span className="text-[22px] leading-none text-muted transition-transform group-open:rotate-45">
                +
              </span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-muted">{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function PeeqdHomeCTA() {
  return (
    <section className="mx-auto max-w-4xl px-6 pb-24 pt-24 text-center">
      <h2
        className="font-display text-4xl font-black tracking-tight md:text-6xl"
        dangerouslySetInnerHTML={{
          __html: 'We analyze. We report.<br><span class="grad-text">You get insights.</span>',
        }}
      />
      <p className="mt-4 text-muted">
        Add an account today - your first activity report starts building the moment you do.
      </p>
      <Link
        href="#"
        className="grad-btn mt-8 inline-block rounded-full px-8 py-4 text-lg font-semibold text-white shadow-lg hover:opacity-90"
      >
        Get your first report
      </Link>
    </section>
  );
}

export function PeeqdFooter() {
  return (
    <footer className="mx-auto mt-6 max-w-6xl border-t border-line py-10">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2.5">
          <PeeqdLogoIcon className="h-7 w-7" />
          <span className="font-display text-[16px] font-extrabold">Peeqd</span>
          {/* <span className="ml-2 text-[12.5px] text-muted">Instagram tracking with real timestamps.</span> */}
        </div>
        <div className="flex items-center gap-5 text-[13px] text-muted">
          <Link href="/pricing" className="transition hover:text-ink">Pricing</Link>
          <Link href="/contact" className="transition hover:text-ink">Contact</Link>
          <Link href="/privacy" className="transition hover:text-ink">Privacy</Link>
          <Link href="/terms" className="transition hover:text-ink">Terms</Link>
        </div>
      </div>
      <p className="mt-6 max-w-3xl text-[11.5px] leading-relaxed text-muted/70">
        Peeqd is not affiliated with, endorsed by, or connected to Instagram or Meta Platforms, Inc. Peeqd analyzes publicly available information only, does not store user media.
      </p>
    </footer>
  );
}
